import React, { useEffect, useState, useCallback } from "react";
import { getGBussinessInfo } from "../services/apiServices";
import tranImg from "./images/Transactions.svg";
import ccImg from "./images/paymentsicons/card-credit.svg";
import cash from "./images/paymentsicons/cash.svg";
import coupon from "./images/paymentsicons/coupon.svg";
import multiple from "./images/paymentsicons/multiple-payments.svg";
import otherpayments from "./images/paymentsicons/other-payment.svg";
import uiImg from "./images/paymentsicons/upi.svg";
import wallet from "./images/paymentsicons/wallet.svg";
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBBtn,
  MDBIcon,
} from "mdb-react-ui-kit";
import {
  isDefaultTransIdRequired,
  multipleSplitRequiresTransId,
  validateBusinessTransId,
} from "../utils/paymentTransIdRules";

function PaymentMethodTile({ className, active, saving, onClick, children }) {
  return (
    <div
      className={className}
      style={{
        position: "relative",
        cursor: saving ? "default" : "pointer",
        opacity: saving ? 0.6 : 1,
        ...(active
          ? {
              boxShadow: "0 0 0 3px #198754, 0 2px 8px rgba(25, 135, 84, 0.25)",
            }
          : {}),
      }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (saving) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      aria-current={active ? "true" : undefined}
    >
      {active && (
        <span
          title="Current payment mode for this line"
          className="payment-mode-edit-current-badge"
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            zIndex: 3,
            background: "#198754",
            color: "#fff",
            borderRadius: "50%",
            width: 22,
            height: 22,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
          }}
        >
          <MDBIcon fas icon="check" style={{ fontSize: "0.65rem" }} />
        </span>
      )}
      {children}
    </div>
  );
}

const defaultPayInfo = () => ({
  cash_amt: "",
  cc_amt: "",
  upi_amt: "",
  upi_input: "",
  wallet_amt: "",
  wallet_input: "",
  userpaymethod: "",
  coupon_input: "",
  coupon_amt: "",
  usr_input: "",
});

/**
 * POS-style payment picker for editing one payment-history line (same visuals as Payments.js).
 */
export default function PaymentModeEditPOS({
  open,
  onClose,
  linePaid,
  initialMode,
  initialPayinfoStr,
  initialTransId,
  gymId,
  onSave,
  saving,
  serverError,
}) {
  const [dpaymethods, setDpaymethods] = useState([]);
  const [dpaymethodsRequireTransId, setDpaymethodsRequireTransId] = useState([]);
  const [userpaymethods, setUserpaymethods] = useState([]);
  const [apiError, setApiError] = useState(false);
  const [showMultiple, setShowMultiple] = useState(false);
  const [showCoupon, setShowCoupon] = useState(false);
  const [balance, setBalance] = useState(0);
  const [balmode, setBalmode] = useState(1);
  const [curSelMode, setCurSelMode] = useState(1);
  const [payInfo, setPayInfo] = useState(defaultPayInfo());
  const [transId, setTransId] = useState("");
  const [showError, setShowError] = useState(false);
  const [userpayselect, setUserpayselect] = useState(null);
  const [customModeOption, setCustomModeOption] = useState(null);
  const [inlineError, setInlineError] = useState("");

  const lineTotal = Number(linePaid) || 0;

  const updatepayInfo = useCallback((patch) => {
    setPayInfo((prev) => ({ ...prev, ...patch }));
  }, []);

  const recomputeBalance = useCallback(
    (pi, mode) => {
      if (mode !== 6) {
        setBalance(0);
        return;
      }
      const sum =
        (parseFloat(pi.cash_amt) || 0) +
        (parseFloat(pi.cc_amt) || 0) +
        (parseFloat(pi.upi_amt) || 0) +
        (parseFloat(pi.wallet_amt) || 0);
      const bal = lineTotal - sum;
      setBalance(bal < 0 ? 0 : bal);
    },
    [lineTotal]
  );

  useEffect(() => {
    if (!gymId) return;
    let cancelled = false;
    (async () => {
      const response = await getGBussinessInfo(gymId);
      if (!response || response === "false") {
        if (!cancelled) setApiError(true);
        return;
      }
      const record = await response.json();
      if (cancelled || !record) return;
      setDpaymethods(record.dpaymethods || []);
      setDpaymethodsRequireTransId(record.dpaymethodsRequireTransId || []);
      setUserpaymethods(record.userpaymethods || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [gymId, open]);

  useEffect(() => {
    if (!open) {
      setInlineError("");
      setUserpayselect(null);
      setCustomModeOption(null);
      return;
    }
    let inf = {};
    try {
      const p = initialPayinfoStr ? JSON.parse(initialPayinfoStr) : {};
      inf = p?.info && typeof p.info === "object" ? p.info : {};
    } catch (e) {
      inf = {};
    }
    const mode = Number(initialMode) || 1;
    setCurSelMode(mode);
    setShowMultiple(mode === 6);
    setShowCoupon(mode === 5);
    setTransId(initialTransId != null ? String(initialTransId) : "");
    setShowError(false);
    setInlineError("");
    setPayInfo({
      ...defaultPayInfo(),
      cash_amt: inf.cash_amt != null && inf.cash_amt !== "" ? String(inf.cash_amt) : "",
      cc_amt: inf.cc_amt != null && inf.cc_amt !== "" ? String(inf.cc_amt) : "",
      upi_amt: inf.upi_amt != null && inf.upi_amt !== "" ? String(inf.upi_amt) : "",
      wallet_amt: inf.wallet_amt != null && inf.wallet_amt !== "" ? String(inf.wallet_amt) : "",
      upi_input: inf.upi_input != null ? String(inf.upi_input) : "",
      wallet_input: inf.wallet_input != null ? String(inf.wallet_input) : "",
      userpaymethod: inf.userpaymethod != null ? String(inf.userpaymethod) : "",
      coupon_input: inf.coupon_input != null ? String(inf.coupon_input) : "",
      coupon_amt: inf.coupon_amt != null && inf.coupon_amt !== "" ? String(inf.coupon_amt) : "",
      usr_input:
        inf.usr_input != null && inf.usr_input !== ""
          ? String(inf.usr_input)
          : String(lineTotal || ""),
    });
    setBalmode(typeof inf.balmode === "number" ? inf.balmode : mode);
    if (mode === 6) {
      const sum =
        (parseFloat(inf.cash_amt) || 0) +
        (parseFloat(inf.cc_amt) || 0) +
        (parseFloat(inf.upi_amt) || 0) +
        (parseFloat(inf.wallet_amt) || 0);
      setBalance(Math.max(0, lineTotal - sum));
    } else {
      setBalance(0);
    }
    setUserpayselect(null);
    setCustomModeOption(null);
  }, [open, initialMode, initialPayinfoStr, initialTransId, linePaid]);

  useEffect(() => {
    if (showMultiple) recomputeBalance(payInfo, 6);
  }, [payInfo, showMultiple, recomputeBalance]);

  const payAmount = (obj, index) => {
    if (obj && obj.option !== undefined && obj.option !== null) {
      setCurSelMode(obj.option);
    } else if (index != null && index >= 7) {
      setCurSelMode(index);
    }

    if (obj && obj.option && [1, 2, 3, 4].includes(obj.option)) {
      const req = isDefaultTransIdRequired(
        obj.option,
        dpaymethodsRequireTransId
      );
      const v = validateBusinessTransId(transId, req);
      if (!v.ok) {
        setShowError(true);
        setInlineError(v.message);
        return;
      }
    } else {
      const v = validateBusinessTransId(transId, false);
      if (!v.ok) {
        setShowError(true);
        setInlineError(v.message);
        return;
      }
    }
    setInlineError("");
    setShowError(false);
    updatepayInfo({ userpaymethod: "" });
    setUserpayselect(null);
    setCustomModeOption(null);

    if (obj && obj.option) {
      if ([1, 2, 3, 4].includes(obj.option)) {
        onSave({
          type: "single",
          newMode: obj.option,
          transId: transId.trim(),
        });
        return;
      }
      if (obj.option === 5) {
        setCurSelMode(5);
        setShowCoupon(true);
        setShowMultiple(false);
        return;
      }
      if (obj.option === 6) {
        setShowMultiple(true);
        setShowCoupon(false);
        setCurSelMode(6);
        recomputeBalance(payInfo, 6);
        return;
      }
    }

    if (obj && index >= 7) {
      setShowMultiple(false);
      setShowCoupon(false);
      setCurSelMode(index);
      setCustomModeOption(obj);
      const defaultAmt = String(lineTotal);
      updatepayInfo({
        userpaymethod: obj.title,
        usr_input: defaultAmt,
      });
      setUserpayselect(
        <CustomAccountAmount
          key="custom"
          label={obj.title}
          initialAmount={defaultAmt}
          onChange={(val) => updatepayInfo({ usr_input: val })}
        />
      );
      setBalance(0);
    }
  };

  const handleChange = (e, value, checkString) => {
    setBalmode(value);
  };

  const confirmMultipleOrCoupon = () => {
    setInlineError("");
    if (showMultiple && curSelMode === 6) {
      const req = multipleSplitRequiresTransId(
        payInfo,
        dpaymethodsRequireTransId
      );
      const v = validateBusinessTransId(transId, req);
      if (!v.ok) {
        setShowError(true);
        setInlineError(v.message);
        return;
      }
    } else if (showCoupon && curSelMode === 5) {
      const req = isDefaultTransIdRequired(5, dpaymethodsRequireTransId);
      const v = validateBusinessTransId(transId, req);
      if (!v.ok) {
        setShowError(true);
        setInlineError(v.message);
        return;
      }
    } else if (customModeOption && curSelMode >= 7) {
      const req = !!customModeOption.requireTransactionId;
      const v = validateBusinessTransId(transId, req);
      if (!v.ok) {
        setShowError(true);
        setInlineError(v.message);
        return;
      }
    } else {
      const v = validateBusinessTransId(transId, false);
      if (!v.ok) {
        setShowError(true);
        setInlineError(v.message);
        return;
      }
    }
    setShowError(false);
    if (showMultiple && curSelMode === 6) {
      const sum =
        (parseFloat(payInfo.cash_amt) || 0) +
        (parseFloat(payInfo.cc_amt) || 0) +
        (parseFloat(payInfo.upi_amt) || 0) +
        (parseFloat(payInfo.wallet_amt) || 0);
      if (Math.abs(sum - lineTotal) > 0.02) {
        setInlineError(`Split total must equal ₹${lineTotal}.`);
        return;
      }
      onSave({
        type: "multiple",
        multipleInfo: {
          cash_amt: payInfo.cash_amt,
          cc_amt: payInfo.cc_amt,
          upi_amt: payInfo.upi_amt,
          upi_input: payInfo.upi_input,
          wallet_amt: payInfo.wallet_amt,
          wallet_input: payInfo.wallet_input,
          userpaymethod: payInfo.userpaymethod || "",
        },
        transId: transId.trim(),
      });
      return;
    }
    if (showCoupon && curSelMode === 5) {
      const camt = parseFloat(payInfo.coupon_amt) || 0;
      if (Math.abs(camt - lineTotal) > 0.02) {
        setInlineError(`Coupon amount must equal ₹${lineTotal}.`);
        return;
      }
      onSave({
        type: "single",
        newMode: 5,
        transId: transId.trim(),
        couponInfo: {
          coupon_input: payInfo.coupon_input,
          coupon_amt: payInfo.coupon_amt,
        },
      });
      return;
    }
    if (customModeOption && curSelMode >= 7) {
      const amt = parseFloat(payInfo.usr_input) || 0;
      if (Math.abs(amt - lineTotal) > 0.02) {
        setInlineError(`Amount must equal ₹${lineTotal} for this line.`);
        return;
      }
      onSave({
        type: "single",
        newMode: curSelMode,
        transId: transId.trim(),
        customInfo: {
          userpaymethod: payInfo.userpaymethod || customModeOption.title,
          usr_input: String(payInfo.usr_input),
        },
      });
    }
  };

  const onTransIdChange = (e) => {
    setTransId(e.target.value);
    setShowError(false);
    setInlineError("");
  };

  if (apiError) {
    return (
      <MDBModal open={open} setOpen={(v) => !v && onClose()} tabIndex="-1">
        <MDBModalDialog>
          <MDBModalContent>
            <MDBModalBody>
              <p className="mb-3">Could not load payment settings. Try again.</p>
              <MDBBtn onClick={onClose}>Close</MDBBtn>
            </MDBModalBody>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    );
  }

  const showFooter = showMultiple || showCoupon || userpayselect;

  const existingModeNum =
    initialMode !== undefined && initialMode !== null && initialMode !== ""
      ? Number(initialMode)
      : NaN;
  const isExistingTile = (modeNum) =>
    Number.isFinite(existingModeNum) && existingModeNum === modeNum;

  return (
    <MDBModal open={open} setOpen={(v) => !v && onClose()} onClose={onClose} tabIndex="-1">
      <MDBModalDialog size="lg" scrollable>
        <MDBModalContent>
          <MDBModalHeader>
            <MDBModalTitle>Payment Information</MDBModalTitle>
            <MDBBtn className="btn-close" color="none" onClick={onClose} disabled={saving} />
          </MDBModalHeader>
          <MDBModalBody>
            <div className="d-flex space-between mb-2 mt-1 mob-block flex-wrap gap-2">
              <p className="mb-0 fs-7 text-muted">All transactions are secure and encrypted.</p>
              <p className="mb-0">
                <b>
                  Total for this line: <img className="w-11" src={tranImg} alt="" /> {lineTotal}
                </b>
              </p>
            </div>

            <div className="d-flex flex-wrap">
              {dpaymethods?.filter((item) => item === 1).length > 0 && (
                <PaymentMethodTile
                  className="paymt-clr-1"
                  active={isExistingTile(1)}
                  saving={saving}
                  onClick={() => !saving && payAmount({ option: 1 })}
                >
                  <img className="w-25" src={cash} alt="Cash" />
                  Cash
                </PaymentMethodTile>
              )}
              {dpaymethods?.filter((item) => item === 2).length > 0 && (
                <PaymentMethodTile
                  className="paymt-clr-2"
                  active={isExistingTile(2)}
                  saving={saving}
                  onClick={() => !saving && payAmount({ option: 2 })}
                >
                  <img className="w-25" src={ccImg} alt="Card" />
                  Card
                </PaymentMethodTile>
              )}
              {dpaymethods?.filter((item) => item === 3).length > 0 && (
                <PaymentMethodTile
                  className="paymt-clr-3"
                  active={isExistingTile(3)}
                  saving={saving}
                  onClick={() => !saving && payAmount({ option: 3 })}
                >
                  <img className="w-25" src={uiImg} alt="UPI" />
                  UPI
                </PaymentMethodTile>
              )}
              {dpaymethods?.filter((item) => item === 4).length > 0 && (
                <PaymentMethodTile
                  className="paymt-clr-4"
                  active={isExistingTile(4)}
                  saving={saving}
                  onClick={() => !saving && payAmount({ option: 4 })}
                >
                  <img className="w-25" src={wallet} alt="Wallet" />
                  Wallet
                </PaymentMethodTile>
              )}
              {dpaymethods?.filter((item) => item === 5).length > 0 && (
                <PaymentMethodTile
                  className="paymt-clr-5"
                  active={isExistingTile(5)}
                  saving={saving}
                  onClick={() => !saving && payAmount({ option: 5 })}
                >
                  <img className="w-25" src={coupon} alt="Coupon" />
                  Coupon
                </PaymentMethodTile>
              )}
              {userpaymethods?.map(
                (item, index) =>
                  item.check && (
                    <PaymentMethodTile
                      key={item._id || index}
                      className={"paymt-clr-" + (index + 7)}
                      active={isExistingTile(index + 7)}
                      saving={saving}
                      onClick={() => !saving && payAmount(item, index + 7)}
                    >
                      <img className="w-25" src={otherpayments} alt={item.title} />
                      {item.title}
                    </PaymentMethodTile>
                  )
              )}
              <PaymentMethodTile
                className="paymt-clr-Multiple"
                active={isExistingTile(6)}
                saving={saving}
                onClick={() => !saving && payAmount({ option: 6 })}
              >
                <img className="w-25" src={multiple} alt="Multiple" />
                Multiple
              </PaymentMethodTile>
            </div>

            <div className="mt-4 mb-3 d-flex flex-wrap align-items-center">
              <label className="me-2 mb-0">
                Transaction ID
                {showMultiple && curSelMode === 6 && multipleSplitRequiresTransId(payInfo, dpaymethodsRequireTransId) ? (
                  <span className="text-danger"> *</span>
                ) : showCoupon && curSelMode === 5 && isDefaultTransIdRequired(5, dpaymethodsRequireTransId) ? (
                  <span className="text-danger"> *</span>
                ) : customModeOption && curSelMode >= 7 && customModeOption.requireTransactionId ? (
                  <span className="text-danger"> *</span>
                ) : (
                  <span className="text-muted small"> (optional)</span>
                )}
              </label>
              <input
                type="text"
                className="form-control"
                style={{ maxWidth: 280 }}
                value={transId}
                disabled={saving}
                onChange={onTransIdChange}
                onBlur={(e) => {
                  const v = validateBusinessTransId(e.target.value, false);
                  if (!v.ok) {
                    setInlineError(v.message);
                    setShowError(true);
                  } else {
                    setShowError(false);
                  }
                }}
              />
            </div>

            {userpayselect && <div className="splitenter">{userpayselect}</div>}

            {showCoupon && (
              <div className="splitenter">
                <span>
                  Enter Coupon Code{" "}
                  <input
                    className="coupen-code"
                    type="text"
                    disabled={saving}
                    value={payInfo.coupon_input}
                    onChange={(e) => updatepayInfo({ coupon_input: e.target.value })}
                    placeholder="Enter Code Here"
                  />
                </span>
                <span className="ms-2">
                  <span>
                    Coupon Amount <img className="w-11" src={tranImg} alt="" />
                  </span>{" "}
                  <input
                    className="splitinput w-50"
                    type="text"
                    disabled={saving}
                    value={payInfo.coupon_amt}
                    onChange={(e) => updatepayInfo({ coupon_amt: e.target.value })}
                    onKeyPress={(event) => {
                      if (!/[0-9.]/.test(event.key)) event.preventDefault();
                    }}
                  />
                </span>
              </div>
            )}

            {showMultiple && (
              <>
                <div className="pt-1 pb-1">
                  Pay balance amount using
                  <div className="duepaymentradio">
                    <div onClick={(e) => handleChange(e, 1, "cash")} style={{ zIndex: 2 }}>
                      <span>Enter cash to be paid</span>
                    </div>
                    <span>
                      <span>
                        <img className="w-11" src={tranImg} alt="" />
                      </span>
                      <input
                        className="splitinput w-50"
                        type="text"
                        disabled={saving}
                        value={payInfo.cash_amt}
                        onChange={(e) => updatepayInfo({ cash_amt: e.target.value })}
                        onKeyPress={(event) => {
                          if (!/[0-9.]/.test(event.key)) event.preventDefault();
                        }}
                      />
                    </span>
                  </div>
                </div>
                <div className="duepaymentradio">
                  <div onClick={(e) => handleChange(e, 2, "cc")} style={{ zIndex: 2 }}>
                    <span>Credit / Debit Card</span>
                  </div>
                  <span>
                    <span>
                      <img className="w-11" src={tranImg} alt="" />
                    </span>
                    <input
                      className="splitinput w-50"
                      type="text"
                      disabled={saving}
                      value={payInfo.cc_amt}
                      onChange={(e) => updatepayInfo({ cc_amt: e.target.value })}
                      onKeyPress={(event) => {
                        if (!/[0-9.]/.test(event.key)) event.preventDefault();
                      }}
                    />
                  </span>
                </div>
                <div className="duepaymentradio">
                  <div onClick={(e) => handleChange(e, 3, "upi")} style={{ zIndex: 2 }}>
                    <span>UPI</span>
                  </div>
                  <div>
                    <span>
                      <input
                        className="splitinputupi"
                        type="text"
                        placeholder="number@bank.com"
                        disabled={saving}
                        value={payInfo.upi_input}
                        onChange={(e) => updatepayInfo({ upi_input: e.target.value })}
                      />
                    </span>
                    <span>
                      <span>
                        <img className="w-11" src={tranImg} alt="" />
                      </span>{" "}
                      <input
                        className="splitinput w-50"
                        type="text"
                        disabled={saving}
                        value={payInfo.upi_amt}
                        onChange={(e) => updatepayInfo({ upi_amt: e.target.value })}
                        onKeyPress={(event) => {
                          if (!/[0-9.]/.test(event.key)) event.preventDefault();
                        }}
                      />
                    </span>
                  </div>
                </div>
                <div className="duepaymentradio">
                  <div onClick={(e) => handleChange(e, 4, "wallet")} style={{ zIndex: 2 }}>
                    <span>Wallets</span>
                  </div>
                  <div>
                    <span>
                      <input
                        className="splitinputwallet splitinputupi"
                        type="text"
                        placeholder="123456789"
                        disabled={saving}
                        value={payInfo.wallet_input}
                        onChange={(e) => updatepayInfo({ wallet_input: e.target.value })}
                      />
                    </span>
                    <span>
                      <span>
                        <img className="w-11" src={tranImg} alt="" />
                      </span>{" "}
                      <input
                        className="splitinput w-50"
                        type="text"
                        disabled={saving}
                        value={payInfo.wallet_amt}
                        onChange={(e) => updatepayInfo({ wallet_amt: e.target.value })}
                        onKeyPress={(event) => {
                          if (!/[0-9.]/.test(event.key)) event.preventDefault();
                        }}
                      />
                    </span>
                  </div>
                </div>
              </>
            )}

            {inlineError && <div className="text-danger my-2">{inlineError}</div>}
            {serverError && (
              <div className="text-danger  my-2">{serverError}</div>
            )}

            {showFooter && (
              <>
                <div className="d-flex space-between mb-2 mt-3 clr-red">
                  <span>Balance due amount</span>
                  <span>
                    <img className="w-11" src={tranImg} alt="" />
                    {showMultiple ? balance : 0}
                  </span>
                </div>
                <div className="d-flex justify-content-end gap-2 pt-2 pb-1">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={saving}
                    onClick={confirmMultipleOrCoupon}
                  >
                    {saving ? "Saving…" : "Done"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={saving}
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </MDBModalBody>
        </MDBModalContent>
      </MDBModalDialog>
    </MDBModal>
  );
}

function CustomAccountAmount({ label, initialAmount, onChange }) {
  const [inputValue, setInputValue] = useState(initialAmount);
  useEffect(() => {
    setInputValue(initialAmount);
  }, [initialAmount]);
  return (
    <>
      <span>{label}</span>
      <span>
        <span>
          Amount <img className="w-11" src={tranImg} alt="" />
        </span>{" "}
        <input
          className="splitinput w-50"
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            onChange(e.target.value);
          }}
          onKeyPress={(event) => {
            if (!/[0-9.]/.test(event.key)) event.preventDefault();
          }}
        />
      </span>
    </>
  );
}
